import { CalendarIcon, EditIcon, GenerateIcon, ImproveIcon, NotificationDefaultIcon, Tab } from '@bp/ui-components';
import classNames from 'classnames';
import { SubmissionStatus } from 'client/gql/graphql';
import { useAssignments } from 'components/Assignments/hooks/useAssignments';
import { BpHeader } from 'components/BpHeader/BpHeader';
import { BpSubpage } from 'components/BpSubpage/BpSubpage';
import { PublicSubmissionsTab } from 'components/Submissions/components/PublicSubmissionsTab/PublicSubmissionsTab';
import { SubmissionsTab } from 'components/Submissions/components/SubmissionsTab/SubmissionsTab';
import { useRequireParams } from 'hooks/useRequireParams';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { lowercaseFirstLetter } from 'utils/stringHelper';
import { AssignmentTab } from '../../../../components/Assignments/components/AssignmentTab/AssignmentTab';
import { DesktopContext } from '../../../../context/IsDesktop';
import { useDays } from '../../../../hooks/useDays';
import styles from './TeacherContent.module.scss';

type CourseAssignmentTeacherContentProps = {
  assignmentUuid: string;
  onEdit: () => void;
};

export const CourseAssignmentTeacherContent = ({ assignmentUuid, onEdit }: CourseAssignmentTeacherContentProps) => {
  const { t } = useTranslation();
  const { courseUuid } = useRequireParams(['courseUuid']);
  const { isDesktop } = useContext(DesktopContext);

  const { assignments, ConfirmationDialog, loading } = useAssignments({ courseUuid, assignmentUuid });

  const { daysLeft, isPast, isFuture } = useDays();

  const assignment = assignments.find((a) => a.uuid === assignmentUuid);
  if (!assignment) throw new Error('Assignment not found');

  const submissions = assignment.submissions ?? [];
  const submissionsNew = submissions.filter((s) => s.status === SubmissionStatus.New);
  const submissionsTodo = submissions.filter(
    (s) =>
      s.status === SubmissionStatus.Todo ||
      s.status === SubmissionStatus.Draft ||
      s.status === SubmissionStatus.Feedback,
  );

  const newSubmissionsText = t('submissions.newSubmissions', { count: submissionsNew.length ?? 0 });
  const trimmedText = newSubmissionsText.substring(newSubmissionsText.indexOf(' ') + 1);

  const isFutureAssignment = isFuture(assignment.visibleFrom.toISOString());
  const isPastAssignment = isPast(assignment.dueDate.toISOString());
  const hasPublicSubmission = submissions.some((s) => s.public);

  return (
    <BpSubpage loading={!!loading} className={styles['course-assignment-teacher']}>
      <div className={styles.header}>
        <BpHeader
          noMargin
          headline={assignment.title ?? t('common.noTitle')}
          showBackButton
          actions={[
            {
              hierarchy: isDesktop ? 'primary' : 'secondary',
              icon: isDesktop ? undefined : <EditIcon />,
              callback: () => onEdit(),
              text: isDesktop ? t('common.editType', { type: t('assignments.title', { count: 1 }) }) : '',
            },
          ]}
        />
      </div>
      <div className={styles.columns}>
        <div className={classNames(styles['overview-card'], styles.dark)}>
          <div className={styles.top}>
            {isDesktop && (
              <div className={styles.icon}>
                <NotificationDefaultIcon />
              </div>
            )}
            {/*TODO questions count*/}
            <div className={styles.count}>0</div>
            <div className={styles.title}>{lowercaseFirstLetter(t('submissions.newQuestions'))}</div>
          </div>
          <div className={styles.bottom}>
            {isDesktop && (
              <div className={styles.icon}>
                <GenerateIcon />
              </div>
            )}
            <div className={styles.count}>{submissionsNew.length ?? 0}</div>
            <div className={styles.title}>{trimmedText}</div>
          </div>
        </div>

        <div className={styles['overview-card']}>
          <div className={styles.top}>
            {isDesktop && (
              <div className={styles.icon}>
                <ImproveIcon />
              </div>
            )}
            <div className={styles.count}>
              {submissionsTodo.length}/{submissions.length}
            </div>
            <div className={styles.title}>{lowercaseFirstLetter(t('common.pending'))}</div>
          </div>
          <div className={styles.bottom}>
            {isDesktop && (
              <div className={styles.icon}>
                <CalendarIcon />
              </div>
            )}
            {isFutureAssignment || isPastAssignment ? (
              <div className={styles.inactive}>
                {isFutureAssignment && t('assignments.notYetActive')}
                {isPastAssignment && t('assignments.finished')}
              </div>
            ) : (
              <>
                <div className={styles.count}>{daysLeft(assignment.dueDate.toISOString())}</div>
                <div className={styles.title}>{t('common.daysLeft')}</div>
              </>
            )}
          </div>
        </div>

        <div className={classNames(styles['overview-card'], styles.unit)}>
          <div className={styles.top}>
            <div className={styles.title}>{t('teachingUnits.titleSingular') + ':'}</div>
            <div className={styles.subject}>{assignment.teachingUnit.title}</div>
          </div>
        </div>
      </div>

      <Tab
        tabs={[
          {
            content: <SubmissionsTab />,
            title: t('submissions.title', { count: 2 }),
            value: 'submissions',
          },
          {
            content: <PublicSubmissionsTab courseUuid={courseUuid} assignmentUuid={assignment.uuid} />,
            title: t('submissions.publicAnswers'),
            value: 'public',
            disabled: !hasPublicSubmission,
          },
          {
            content: <AssignmentTab assignmentUuid={assignment.uuid} />,
            title: t('assignments.title', { count: 1 }),
            value: 'assignments',
          },
        ]}
      />

      <ConfirmationDialog />
    </BpSubpage>
  );
};
